import React, { Component, Fragment } from "react";
import Breadcrumb from "../common/breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Truck, ArrowLeft, AlertCircle } from "react-feather";
import KidoLoader from "../common/KidoLoader";
import { PaymentSummary, ReceivePanel, ConsignmentList } from "./SteadfastParts";
import "./steadfast-kido.css";
import { getSingleOrder, applyPaymentBatch, getProcessedSteadfastPaymentIds, restoreProductStockForCancelledOrder } from "../../firebase/firebase.utils";

const api_key    = process.env.REACT_APP_STEADFAST_API_KEY || "";
const secret_key = process.env.REACT_APP_STEADFAST_SECRET_KEY || "";
const base_url   = "https://portal.packzy.com/api/v1";

// Steadfast status → our DB orderStatus value
const SF_TO_ORDER_STATUS = {
  "delivered":         "Delivered",
  "partial_delivered": "Partial Delivered",
  "cancelled":         "Cancelled",
};

class SteadfastPaymentDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      payment: null,
      error: null,
      receivePreview: null,
      receiveLoading: false,
      receiveConfirming: false,
      processed: false,
    };
  }

  componentDidMount() {
    const paymentId = this.props.match?.params?.id;
    if (paymentId) {
      this.fetchPayment(paymentId);
      this.checkProcessed(paymentId);
    }
  }

  checkProcessed = async (paymentId) => {
    const ids = await getProcessedSteadfastPaymentIds();
    if (ids.has(String(paymentId))) {
      this.setState({ processed: true });
    }
  };

  getMonthName = () => {
    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const d = new Date();
    return `${months[d.getMonth()]},${d.getFullYear()}`;
  };

  fetchPayment = async (paymentId) => {
    this.setState({ loading: true, error: null });
    try {
      const res = await axios.get(`${base_url}/payments/${paymentId}`, {
        headers: { "Api-Key": api_key, "Secret-Key": secret_key, "Content-Type": "application/json" },
      });
      this.setState({ payment: res.data.payment, loading: false });
    } catch (e) {
      this.setState({
        error: "Failed to load payment: " + (e.response?.data?.message || e.message),
        loading: false,
      });
    }
  };

  handleReceivePayment = async () => {
    const { payment } = this.state;
    const consignments = payment.consignments || [];
    this.setState({ receiveLoading: true, receivePreview: null });

    const preview = await Promise.all(consignments.map(async (c) => {
      try {
        const orderData = await getSingleOrder(String(c.invoice));
        if (!orderData) return { consignment: c, notFound: true };

        const order = { id: String(c.invoice), ...orderData };
        const totalBill =
          Number(order.subTotal || 0) +
          Number(order.deliveryCharge || 0) -
          Number(order.discountApplied || 0) -
          Number(order.couponApplied ? order.couponApplied.discount || 0 : 0) -
          Number(order.finalDiscount || 0);
        const alreadyPaid = (order.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
        const totalDue    = totalBill - alreadyPaid;

        // This specific consignment was already recorded → skip
        const alreadyApplied = (order.payments || []).some(
          (p) => String(p.transactionId) === String(c.consignment_id)
        );

        // Order already fully settled → skip
        const alreadyFullyPaid = totalDue <= 0;

        // Map Steadfast delivery status to our DB orderStatus
        const sfOrderStatus = SF_TO_ORDER_STATUS[c.status] || null;

        // Cancelled consignments never had COD money received — no payment/cash-in, order status only
        const isCancelled = c.status === "cancelled";
        const alreadyCancelled = isCancelled && order.orderStatus === "Cancelled";

        // Cap COD at what's actually owed — never overpay
        const appliedAmount = isCancelled ? 0 : Math.max(0, Math.min(Number(c.cod_amount || 0), totalDue));

        const newTotalPaid   = alreadyPaid + appliedAmount;
        const paymentStatus  = isCancelled
          ? (order.paymentStatus || "Not Paid")
          : (newTotalPaid >= totalBill ? "Paid" : "Partially Paid");

        return {
          consignment: c,
          order,
          totalBill,
          alreadyPaid,
          totalDue,
          appliedAmount,
          paymentStatus,
          sfOrderStatus,
          isCancelled,
          alreadyCancelled,
          alreadyApplied,
          alreadyFullyPaid,
        };
      } catch {
        return { consignment: c, notFound: true };
      }
    }));

    this.setState({ receivePreview: preview, receiveLoading: false });
  };

  handleConfirmReceive = async () => {
    const { payment, receivePreview } = this.state;
    const adminName = (this.props.currentAdmin && this.props.currentAdmin.name) || "";
    const date = new Date();

    // Skip: not found, already applied to this consignment ID, or order already fully paid
    const applicable = receivePreview.filter(
      (i) => !i.notFound && !i.alreadyApplied && !i.alreadyFullyPaid && !i.isCancelled && i.appliedAmount > 0
    );
    // Cancelled consignments: no COD received — only sync orderStatus to "Cancelled", no payment/cash-in
    const cancelOnly = receivePreview.filter(
      (i) => !i.notFound && i.isCancelled && !i.alreadyCancelled
    );

    if (applicable.length === 0 && cancelOnly.length === 0) {
      toast.error("No applicable consignments to process.");
      return;
    }

    this.setState({ receiveConfirming: true });

    // Deterministic ids (not Date.now()-based) so re-confirming the same
    // Steadfast payment — e.g. after a page refresh, since `processed` is
    // just in-memory state — overwrites the same cash-in/expense docs
    // instead of creating duplicates. Per-order payments are additionally
    // protected inside applyPaymentBatch itself via a dedupe check against
    // live Firestore state, so retrying after a partial failure can't
    // double-apply a consignment even with a stale client-side preview.
    const ordersToUpdate = [
      ...applicable.map(({ consignment: c, order, paymentStatus, appliedAmount, sfOrderStatus }) => {
        const newPaymentEntry = {
          amount: appliedAmount,
          method: "SteadFast (COD)",
          receivedBy: adminName,
          date: date.toLocaleDateString("en-GB"),
          transactionId: String(c.consignment_id),
        };
        const payments = [...(order.payments || []), newPaymentEntry];
        const updatedOrder = { ...order, paymentStatus, payments };
        const patch = { paymentStatus, payments };
        // Only update orderStatus when Steadfast confirms delivery/partial delivery
        if (sfOrderStatus) {
          updatedOrder.orderStatus = sfOrderStatus;
          patch.orderStatus = sfOrderStatus;
        }
        return {
          updatedOrder,
          patch,
          dedupe: { type: "payment", transactionId: String(c.consignment_id) },
          cashIn: {
            id: `steadfast_${c.consignment_id}`,
            category: "ORDERS",
            subCategory: "SteadFast (COD)",
            month: this.getMonthName(),
            date: date.toLocaleDateString("en-GB"),
            note: `Order Id:${order.id}`,
            amount: appliedAmount,
            receiveBy: adminName,
            status: "pending",
            unEditable: true,
          },
        };
      }),
      ...cancelOnly.map(({ order, sfOrderStatus }) => ({
        updatedOrder: { ...order, orderStatus: sfOrderStatus },
        patch: { orderStatus: sfOrderStatus },
        dedupe: { type: "orderStatus", value: sfOrderStatus },
      })),
    ];

    const cashOutAmount = Number(payment.charges || 0) + Number(payment.due_bills || 0);
    const expense = {
      id: `steadfast_payment_${payment.payment_id}`,
      category: "Delivery charge",
      subCategory: String(payment.payment_id),
      month: this.getMonthName(),
      date: date.toLocaleDateString("en-GB"),
      note: `Steadfast Payment #${payment.payment_id} | ${applicable.length} consignments | Total: ৳${payment.total} | Charges: ৳${payment.charges} | Due Bills: ${payment.due_bills}`,
      amount: cashOutAmount,
      receiveBy: adminName,
      status: "pending",
      unEditable: true,
    };

    try {
      for (const { order } of cancelOnly) {
        await restoreProductStockForCancelledOrder(order, {
          cancelNote: "Cancelled via Steadfast courier update",
          adminName,
        });
      }
      const { appliedCount, skippedCount } = await applyPaymentBatch({ ordersToUpdate, expense });
      this.setState({ processed: true, receivePreview: null, receiveConfirming: false });
      toast.success(
        `Applied for ${appliedCount} consignment(s)${skippedCount > 0 ? ` (${skippedCount} already applied, skipped)` : ""}. Cash-out: ৳${cashOutAmount}.`
      );
    } catch (e) {
      toast.error(
        "Failed partway through — some of this may already be saved. Click Receive Payment to refresh and retry what's left. " + (e.message || String(e))
      );
      this.setState({ receiveConfirming: false, receivePreview: null });
    }
  };

  render() {
    const { loading, payment, error, receivePreview, receiveLoading, receiveConfirming, processed } = this.state;
    const paymentId = this.props.match?.params?.id;

    return (
      <Fragment>
        <Breadcrumb title={`Payment ${paymentId}`} parent="Steadfast" />
        <div className="container-fluid">
          <section className="card kd-orders-card kd-sf">
            <div className="kd-ord-top">
              <div className="kd-ord-title">
                <span className="kd-ord-title-tile kd-st--sky" aria-hidden="true">
                  <Truck size={20} />
                </span>
                <div>
                  <h2>
                    Steadfast payment <span className="kd-sf-pid">#{paymentId}</span>
                  </h2>
                  <svg className="kd-squiggle" viewBox="0 0 120 10" aria-hidden="true" focusable="false">
                    <path d="M2 6 Q12 1 22 6 T42 6 T62 6 T82 6 T102 6 T118 5" />
                  </svg>
                </div>
              </div>
              <Link to={`${process.env.PUBLIC_URL}/steadfast/payments`} className="btn btn-light kd-sf-back">
                <ArrowLeft size={15} aria-hidden="true" />
                All payments
              </Link>
            </div>

            <div className="kd-sf-body">
              {loading && (
                <div className="kd-sf-loading">
                  <KidoLoader inline label="Loading payment" />
                  <p>Loading payment details…</p>
                </div>
              )}

              {error && (
                <div className="kd-sf-error" role="alert">
                  <AlertCircle size={18} aria-hidden="true" />
                  {error}
                </div>
              )}

              {!loading && payment && (
                <div className="kd-sf-detail is-page">
                  <PaymentSummary payment={payment} />
                  {(payment.consignments || []).length > 0 && (
                    <ReceivePanel
                      detailed
                      payment={payment}
                      processed={processed}
                      preview={receivePreview}
                      loading={receiveLoading}
                      confirming={receiveConfirming}
                      onReceive={this.handleReceivePayment}
                      onConfirm={this.handleConfirmReceive}
                      onCancel={() => this.setState({ receivePreview: null })}
                    />
                  )}
                  <ConsignmentList consignments={payment.consignments || []} />
                </div>
              )}
            </div>
          </section>
          <ToastContainer />
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({ currentAdmin: state.admins.currentAdmin });
export default connect(mapStateToProps)(SteadfastPaymentDetail);
